import React, { useContext, useEffect, useState } from 'react'
import { ShopContext } from '../context/ShopContext'
import Title from '../components/Title';
import { Helmet } from 'react-helmet';
import axios from 'axios';

const Orders = () => {
  const {backendUrl, token, currency, navigate, products} = useContext(ShopContext);
  const [orderData, setOrderData] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadOrderData = async () => {
    try {
      if (!token) return;
      
      const response = await axios.post(
        backendUrl + '/api/order/userorders',
        {},
        {headers: {token}}
      );

      if (response.data.success) {
        let allOrderItem = [];
        response.data.orders.forEach((order) => {
          order.items.forEach((item) => {
            allOrderItem.push({
              ...item,
              status: order.status,
              payment: order.payment,
              paymentMethod: order.paymentMethod,
              date: order.date,
              orderAmount: order.amount // Add order amount to each item
            });
          });
        });
        setOrderData(allOrderItem.reverse());
      }
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadOrderData();
  }, [token]);

  // Status color configuration
  const getStatusColor = (status) => {
    switch(status) {
      case 'Delivered': return 'bg-green-100 text-green-800';
      case 'Cancelled': return 'bg-red-100 text-red-800';
      case 'Shipped': return 'bg-blue-100 text-blue-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-500"></div>
      </div>
    );
  }

  return (
    <div className='border-t pt-16 pb-12 px-4 sm:px-6'>
      <Helmet>
        <title>My Orders - Sahla Curtain Center</title>
      </Helmet>
      <div className='max-w-6xl mx-auto'>
        <div className='text-2xl mb-8'>
          <Title text1={'MY'} text2={'ORDERS'} />
        </div>
        {orderData.length === 0 ? (
          <div className="text-center py-12 animate-fade-in">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">No orders found</h3>
            <p className="mt-2 text-gray-500">You haven't placed any orders yet.</p>
            <button
              onClick={() => navigate('/')}
              className="mt-6 px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
              Continue Shopping
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {orderData.map((item, index) => {
              // Find product details for image and name
              const product = products.find(p => p._id === item.productId);
              return (
                <div 
                  key={index} 
                  className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="p-4 sm:p-6 flex flex-col md:flex-row gap-6">
                    {/* Product Image */}
                    <div className="flex-shrink-0">
                      <img 
                        className='w-24 h-24 object-cover rounded-lg transition-transform duration-300 hover:scale-105' 
                        src={product && product.image ? product.image[0] : '/no-image.jpg'} 
                        alt={product ? product.name : 'Product'} 
                      />
                    </div>

                    {/* Product Details */}
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900 mb-1">{product ? product.name : 'Product'}</h3>
                      <div className="flex flex-wrap gap-3 mt-2 text-sm">
                        <p className="text-gray-800">{currency}{product ? product.price : ''}</p>
                        <p className="text-gray-600">Qty: {item.quantity}</p>
                        {item.size && <p className="text-gray-600">Size: {item.size}</p>}
                        {product && product.category && <p className="text-gray-600">Category: {product.category}</p>}
                        {product && product.subCategory && <p className="text-gray-600">Subcategory: {product.subCategory}</p>}
                      </div>
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Order Date</p>
                          <p className="text-gray-700">{new Date(item.date).toLocaleDateString()}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Payment Method</p>
                          <p className="text-gray-700 capitalize">{item.paymentMethod}</p>
                        </div>
                      </div>
                    </div>

                    {/* Status & Actions */}
                    <div className="flex flex-col items-end justify-between">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                        {item.status}
                      </span>
                      <button 
                        onClick={() => {
                          navigate(`/order/${item.productId}/date/${item.date}`);
                          localStorage.setItem('orderData', JSON.stringify(item));
                        }}
                        className="mt-4 px-4 py-2 bg-gray-800 text-white text-sm font-medium rounded-md hover:bg-gray-900 transition-colors"
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default Orders